import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import PageShell from "../components/sections/PageShell";
import { Tape, Scribble } from "../components/brand/Doodles";
import { Mascot } from "../components/brand/Mascot";
import { applySeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/notes`;
export const NOTES_SEO = {
  path: "/notes",
  title: "Notes from the Shop — Gadget Guy Media · Gallatin, TN",
  description: "Short build stories from Robert at Gadget Guy Media in Gallatin, TN: what I'm building, what broke, and what I learned along the way.",
};
const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
const TILTS = [-0.6, 0.5, -0.3, 0.7];

const NoteLink = ({ url, label, id }) => {
  const cls = "mt-5 inline-flex items-center gap-2 ggm-hand text-lg";
  const inner = <>{label} <ArrowRight size={16} /></>;
  return url.startsWith("/") ? (
    <Link to={url} className={cls} data-testid={`note-link-${id}`}>{inner}</Link>
  ) : (
    <a href={url} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`note-link-${id}`}>{inner}</a>
  );
};

export const NoteEntry = ({ note, index = 0 }) => (
  <article
    id={note.slug}
    className="ggm-paper relative rounded-sm p-6 md:p-10 scroll-mt-28"
    style={{ transform: `rotate(${TILTS[index % TILTS.length]}deg)`, boxShadow: "0 22px 44px -26px rgba(0,0,0,0.8)" }}
    data-testid={`note-${note.slug}`}
  >
    <Tape style={{ left: index % 2 ? "22%" : "78%", background: "rgba(11,60,220,0.14)" }} />
    <a href={`#${note.slug}`} className="ggm-hand text-base" data-testid={`note-date-${note.slug}`}>
      {note.published_at ? fmt(note.published_at) : "Draft"}
    </a>
    <h2 className="mt-2 font-condensed font-bold uppercase leading-[1.05]" style={{ fontSize: "clamp(1.5rem, 3.2vw, 2.25rem)", color: "var(--ggm-navy)" }}>
      {note.title}
    </h2>
    {note.has_image && (
      <figure className="ggm-polaroid relative mt-6 mx-auto max-w-xl" style={{ transform: `rotate(${index % 2 ? -1.5 : 1.5}deg)` }}>
        <Tape />
        <img src={`${API}/${note.id}/image?v=${encodeURIComponent(note.updated_at)}`} alt={note.image_alt || note.title} loading="lazy" className="w-full h-auto block min-h-[180px]" style={{ background: "var(--ggm-tech-light)" }} data-testid={`note-image-${note.slug}`} />
        {note.image_alt && <figcaption className="ggm-hand pt-2 text-center text-base" style={{ color: "var(--ggm-navy)" }}>{note.image_alt}</figcaption>}
      </figure>
    )}
    <div className="mt-6 space-y-4 font-body text-base md:text-lg leading-relaxed max-w-3xl" style={{ color: "rgba(11,30,58,0.85)" }}>
      {note.body.split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}
    </div>
    {note.link_url && note.link_label && <NoteLink url={note.link_url} label={note.link_label} id={note.slug} />}
    <p className="mt-6 ggm-hand text-lg">— Robert</p>
  </article>
);

export default function NotesPage() {
  const [notes, setNotes] = useState(null);
  useEffect(() => applySeo(NOTES_SEO), []);
  useEffect(() => {
    axios.get(API).then(({ data }) => setNotes(data)).catch(() => setNotes([]));
  }, []);
  useEffect(() => {
    if (!notes?.length || !window.location.hash) return;
    document.getElementById(window.location.hash.slice(1))?.scrollIntoView();
  }, [notes]);

  return (
    <PageShell testId="notes-page">
      <section className="max-w-4xl mx-auto px-5 md:px-8" style={{ paddingTop: "clamp(48px, 8vh, 96px)" }} data-testid="notes-hero">
        <div className="grid md:grid-cols-[1fr_auto] items-end gap-6">
          <div>
            <div className="ggm-eyebrow mb-5">Build journal · Gallatin, TN</div>
            <h1 className="font-condensed font-bold uppercase text-white leading-[1.02]" style={{ fontSize: "clamp(2.4rem, 6vw, 4.5rem)", letterSpacing: "-0.015em" }} data-testid="notes-h1">
              Notes from the shop
            </h1>
            <Scribble className="mt-3 w-48 md:w-64 h-4 block" />
            <p className="mt-6 font-body text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">
              Short stories from the workbench: what I'm building, what broke, and what I learned. Written by me, not a marketing team.
            </p>
          </div>
          <div className="hidden md:block" style={{ transform: "rotate(-4deg)" }}>
            <Mascot pose="laptop" size="md" style={{ height: 170, width: "auto" }} />
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20 space-y-14" data-testid="notes-list">
        {notes === null && <p className="font-body text-white/60" data-testid="notes-loading">Loading notes…</p>}
        {notes?.length === 0 && (
          <p className="ggm-hand text-xl" data-testid="notes-empty">First note coming soon. Check back shortly.</p>
        )}
        {notes?.map((n, i) => <NoteEntry key={n.id} note={n} index={i} />)}
      </section>
    </PageShell>
  );
}
